import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
const BookingSummary = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const [hotelMap, setHotelMap] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch hotel list
        const hotelRes = await fetch("https://api.esthell.com/Hotel");
        const hotelData = await hotelRes.json();
        const hotelMapData = {};
        if (hotelData.success && Array.isArray(hotelData.data)) {
          hotelData.data.forEach((hotel) => {
            hotelMapData[hotel.hotel_id] = hotel.name;
          });
          setHotelMap(hotelMapData);
        }

        // Fetch booking data
        const bookingRes = await fetch(
          "https://api.esthell.com/BookingSummary",
        );
        if (!bookingRes.ok) throw new Error("Failed to fetch bookings");

        const bookingData = await bookingRes.json();
        if (bookingData.success && Array.isArray(bookingData.data)) {
          const transformed = bookingData.data.map((item) => ({
            ...item,
            hotel_name: hotelMapData[item.hotel_id] || item.hotel_id,
          }));
          setBookings(transformed);
        } else {
          throw new Error(bookingData.message || "No booking data available");
        }
      } catch (error) {
        setNotification({
          open: true,
          message: error.message,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const columns = [
    { title: "Booking ID", field: "booking_id" },
    { title: "Hotel", field: "hotel_name" },
    { title: "Channel ID", field: "channel_id" },
    { title: "Room Type", field: "room_type" },
    { title: "Number of Rooms", field: "number_of_rooms", type: "numeric" },
    {
      title: "Booking Date",
      field: "booking_date",
      render: (rowData) =>
        new Date(rowData.booking_date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: "Check In",
      field: "check_in",
      render: (rowData) =>
        new Date(rowData.check_in).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: "Check Out",
      field: "check_out",
      render: (rowData) =>
        new Date(rowData.check_out).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: "Status",
      field: "booking_status",
      render: (rowData) => (
        <span
          style={{
            color: rowData.booking_status === "true" ? "#2e7d32" : "#c62828",
          }}
        >
          {rowData.booking_status}
        </span>
      ),
    },
  ];
  const handleExportToExcel = () => {
    const exportData = bookings.map((item) => ({
      "Booking ID": item.booking_id,
      Hotel: item.hotel_name,
      "Channel ID": item.channel_id,
      "Room Type": item.room_type,
      "Number of Rooms": item.number_of_rooms,
      "Booking Date": item.booking_date,
      "Check In": item.check_in,
      "Check Out": item.check_out,
      Status: item.booking_status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Booking Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(
      blob,
      `Booking_Summary_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{ mb: 3, color: "#1a237e", fontWeight: "bold" }}
      >
        Booking Summary
      </Typography>
      {!loading && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportToExcel}
          >
            Export to Excel
          </Button>
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <MaterialTable
          columns={columns}
          data={bookings}
          actions={false}
          selection={false}
          onEdit={null}
          onDelete={null}
          toolbar={{ create: false }}
          enableRowActions={false}
          enableEditing={false}
        />
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingSummary;
